import {
  alpha,
  Box,
  InputAdornment,
  TextFieldProps,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { KeyboardEvent, RefObject, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { KeyOutlined } from "@mui/icons-material";
import { last } from "lodash-es";
import { RiskFactorsConditionValuesNameType } from "~/pages/security-model/RiskFactorsConfiguration/ConditionBuilder/types";
import { ConditionTagBaseAutocomplete } from "~/components/ConditionBuilder/ConditionTagAutocomplete/ConditionTagBaseAutocomplete";
import { ConditionTagBaseMenuList } from "~/components/ConditionBuilder/ConditionTagAutocomplete/ConditionTagBaseMenuList";
import { ConditionTagMenuItemsGroup } from "~/components/ConditionBuilder/ConditionTagAutocomplete/ConditionTagMenuItemsGroup";
import { ConditionTagCheckboxMenuItem } from "~/components/ConditionBuilder/ConditionTagAutocomplete/ConditionTagCheckboxMenuItem";
import { ConditionTagOpenDropdownButton } from "~/components/ConditionBuilder/ConditionTagOpenDropdownButton/ConditionTagOpenDropdownButton";
import { RiskFactorsConfigurationInput } from "~/pages/security-model/RiskFactorsConfiguration/ConfigurationForm/RiskFactorsConfigurationForm";
import { ApostrophesIcon } from "~/icons";

type ValuesDropdownAutocompleteProps = {
  values: string[];
  name: RiskFactorsConditionValuesNameType;
  conditionFormDictionaryKey: string;
  options: Array<{ label: string; value: string }>;
  anchorRef: RefObject<HTMLButtonElement | null>;
  onDropdownSearchFieldKeyUp: React.KeyboardEventHandler<HTMLInputElement>;
  onDropdownSearchFieldChange: TextFieldProps["onChange"];
  isValuesDropdownOpen: boolean;
  isDictionaryKeySelected: boolean;
  onValuesDropdownOpen: () => void;
  onValuesDropdownClose: () => void;
  onItemClick: (value: string) => void;
  handleKeyValueKeyClick: (value: string) => void;
  handleKeyValueValueClick: (value: string) => void;
};

export const RiskFactorsConditionTagValueAutocomplete = ({
  values,
  name,
  conditionFormDictionaryKey,
  options,
  anchorRef,
  onDropdownSearchFieldKeyUp,
  onDropdownSearchFieldChange,
  onItemClick,
  isValuesDropdownOpen,
  isDictionaryKeySelected,
  onValuesDropdownOpen,
  onValuesDropdownClose,
  handleKeyValueKeyClick,
  handleKeyValueValueClick,
}: ValuesDropdownAutocompleteProps) => {
  const { register, trigger } = useFormContext<RiskFactorsConfigurationInput>();
  const theme = useTheme();

  const lastItemLabel = last(options)?.label;

  const handleKeyDown = (
    event: KeyboardEvent<HTMLLIElement>,
    label: string,
  ): void => {
    // Check if `Tab` is pressed on the last item
    if (event.key === "Tab") {
      if (label === lastItemLabel && !event.shiftKey) {
        // Close dropdown if Tab is pressed on the last item
        onValuesDropdownClose();
      }
    }
  };

  useEffect(() => {
    register(name, {
      // required: "This condition is incomplete. Please add a selection.",
    });
    trigger(name);
  }, [register]);

  return (
    <Box>
      <ConditionTagOpenDropdownButton
        isOpen={isValuesDropdownOpen}
        onClick={
          isValuesDropdownOpen ? onValuesDropdownClose : onValuesDropdownOpen
        }
        data-testid={`${isValuesDropdownOpen ? "close" : "open"}-values-dropdown`}
      />
      <Box sx={{ maxWidth: "400px" }}>
        {isDictionaryKeySelected ? (
          <ConditionTagBaseAutocomplete
            showKeyboardHint
            open={isValuesDropdownOpen}
            onClose={onValuesDropdownClose}
            onDropdownSearchFieldKeyUp={onDropdownSearchFieldKeyUp}
            anchorRef={anchorRef}
            onSearchInputChange={onDropdownSearchFieldChange}
            startAdornment={
              <>
                <InputAdornment
                  position="start"
                  onClick={() => {
                    if (!conditionFormDictionaryKey) return;
                    handleKeyValueKeyClick("");
                  }}
                  sx={[
                    conditionFormDictionaryKey
                      ? {
                          height: "40px",
                          maxHeight: "unset",
                          marginLeft: "-8px",
                          cursor: "pointer",
                          borderBottomLeftRadius: "4px",
                          borderTopLeftRadius: "4px",
                          padding: "0 8px",
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.5,
                          ),
                          zIndex: 2,
                          border: "1px solid",
                          borderColor: theme.palette.primary.main,
                        }
                      : {},
                  ]}
                >
                  {conditionFormDictionaryKey ? (
                    <Typography fontWeight={700}>
                      {conditionFormDictionaryKey}
                    </Typography>
                  ) : (
                    <KeyOutlined sx={{ fontSize: "20px" }} />
                  )}
                </InputAdornment>
                {conditionFormDictionaryKey && (
                  <ApostrophesIcon sx={{ fontSize: "16px", mr: 1 }} />
                )}
              </>
            }
            renderContent={() => (
              <ConditionTagBaseMenuList sx={{ mt: 2 }}>
                <ConditionTagMenuItemsGroup
                  groupTitle={
                    conditionFormDictionaryKey
                      ? "SEARCH RESULTS FOR VALUES MATCHING THIS KEY"
                      : "SEARCH RESULTS FOR KEYS"
                  }
                  onClick={
                    conditionFormDictionaryKey
                      ? handleKeyValueValueClick
                      : handleKeyValueKeyClick
                  }
                  onKeyDown={handleKeyDown}
                  items={options}
                />
              </ConditionTagBaseMenuList>
            )}
          />
        ) : (
          <ConditionTagBaseAutocomplete
            showKeyboardHint
            open={isValuesDropdownOpen}
            onClose={onValuesDropdownClose}
            onDropdownSearchFieldKeyUp={onDropdownSearchFieldKeyUp}
            anchorRef={anchorRef}
            onSearchInputChange={onDropdownSearchFieldChange}
            renderContent={() => (
              <ConditionTagBaseMenuList sx={{ mt: 2 }}>
                {options.map((option) => (
                  <ConditionTagCheckboxMenuItem
                    label={option.label}
                    value={option.value}
                    isChecked={values.includes(option.value)}
                    onClick={onItemClick}
                    key={option.value}
                    onKeyDown={handleKeyDown}
                  />
                ))}
              </ConditionTagBaseMenuList>
            )}
          />
        )}
      </Box>
    </Box>
  );
};
